
import { useEffect, useState } from 'react';
import Vapi from '@vapi-ai/web';
import useServerApi from '../../../hooks/useServerApi';
import { Button, useToast } from '@chakra-ui/react';
import { PhoneIcon, CloseIcon } from '@chakra-ui/icons';

import { useUserData } from 'context/UserDataProvider';
import type { UserData } from 'hooks/useGetUserData';
interface BrowserCallProps {
    id: string;
    type?: string;
    onStop?: () => void;
}

interface Assistant {
    assistant_id: string;
    name: string;
    public_key: string;
}

const Vapicall = ({ id, type, onStop }: BrowserCallProps) => {
    const { userData } = useUserData();
    let myUserData = userData as UserData | null;

    const [assistantData, setAssistantData] = useState<Assistant | null>(null);
    const [{ get }] = useServerApi();
    const [talking, setTalking] = useState(false);
    const [vapiInstance, setVapiInstance] = useState<Vapi | null>(null);

    useEffect(() => {
        async function fetchAssistantData() {
            if (!id) return;
            const assistantIdUrl = `/assistant_info/${id}`;
            const [success, responseData] = await get(assistantIdUrl);
            if (success) {
                setAssistantData(responseData as Assistant);
            } else {
                setAssistantData(null);
            }
        }
        fetchAssistantData();
    }, [id, get]);

    const start = async () => {

        if (!assistantData) return;
        if (talking) return;
        if (!myUserData?.minutes_info) {
            console.error('User data or minutes info is not available');
            return;
        }
        const vapi = new Vapi(assistantData.public_key);
        vapi.on('call-start', () => console.log('Call started'));
        vapi.on('call-end', () => {
            console.log('Call ended');
            setTalking(false);
            setVapiInstance(null);
            //fetchUserData();
            if (onStop) onStop();
        });
        vapi.on('message', (message: any) => {
            if (message.type === 'transcript') {
                //console.log(`${message.role}: ${message.transcript}`);
            }
        });
        await vapi.start(assistantData.assistant_id);
        setVapiInstance(vapi);
        setTalking(true);
    };

    const stop = async () => {
        if (vapiInstance) {
            await vapiInstance.stop();
        }
        setTalking(false);
        setVapiInstance(null);
        if (onStop) onStop();
    };

    const enabled = !!assistantData?.public_key;
    const callType = type === 'vapi_banner' ? 'banner' : type === 'demo_page' ? 'demo_page' : 'agent';
    return <CallButton start={start} stop={stop} enabled={enabled} type={callType} />;
};



const BrowserCall = ({ id, type }: BrowserCallProps): JSX.Element => {
    if (type === 'vapi' || type === 'vapi_banner' || type === 'demo_page') {
        return <Vapicall id={id} type={type} />;
    }
    return <></>;
};


interface CallButtonProps {
    start: () => void;
    stop: () => void;
    enabled?: boolean;
    type: 'banner' | 'agent' | 'demo_page';
}


const CallButton = ({ start, stop, enabled, type }: CallButtonProps): JSX.Element => {
    const { userData } = useUserData();
    let myUserData = userData as UserData | null;
    const toast = useToast();
    const [talking, setTalking] = useState(false);

    const onStart = () => {
        if (!myUserData?.minutes_info) {
            toast({
                title: 'No minutes available',
                description: 'You do not have any minutes left to make a call.',
                status: 'error',
                isClosable: true,
            });
            return;
        }
        try {
            if (!talking) {
                setTalking(true);
                start();
            }
        } catch (e) {
            setTalking(false);
        }
    };
    const onStop = () => {
        setTalking(false);
        stop();
    };
    if (!enabled) {
        return <></>;
    }

    if (type === 'demo_page') {
        return talking ? (
            <Button
                leftIcon={<CloseIcon />}
                colorScheme="red"
                size="lg"
                w="100%"
                fontWeight="semibold"
                fontSize="lg"
                py={6}
                mb={2}
                px={4}
                onClick={onStop}
            >
                Stop Voice Conversation
            </Button>
        ) : (
            <Button
                leftIcon={<PhoneIcon />}
                colorScheme="purple"
                size="lg"
                w="100%"
                fontWeight="semibold"
                fontSize="lg"
                py={6}
                mb={2}
                px={4}
                onClick={onStart}
            >
                Start Voice Conversation
            </Button>
        );
    }

    const ShowBannerVersion = () => (
        talking ? (
            <Button
                bg="white"
                color="#6C63FF"
                fontWeight="bold"
                mr={3}
                _hover={{ bg: '#E0E7FF' }}
                leftIcon={<CloseIcon />}
                onClick={onStop}
            >
                Hang up
            </Button>
        ) : (
            <Button
                bg="white"
                color="#6C63FF"
                fontWeight="bold"
                mr={3}
                _hover={{ bg: '#E0E7FF' }}
                onClick={onStart}
            >
                Talk to agent
            </Button>
        )
    );
    const ShowAgentVersion = () => (
        talking ? (
            <Button size='sm' leftIcon={<CloseIcon />} colorScheme='red' onClick={onStop}>
                Hang up
            </Button>
        ) : (
            <Button size='sm' leftIcon={<PhoneIcon />} colorScheme='green' onClick={onStart}>
                Call Assistant
            </Button>
        )
    );
    return type === 'banner' ? <ShowBannerVersion /> : <ShowAgentVersion />;
};

export default BrowserCall;
